"use client";
import React, { useCallback, useState, useTransition } from "react";
import { Loader2 } from "lucide-react";
import { Modal } from "modules/common/components/Modal/Modal";
import { ICON_SIZE } from "modules/common/components/navigation/Menu";
import { useConfirmDeleteModal } from "modules/common/stores/useConfirmDeleteStore";
import type { TradeListType } from "modules/tradeLists/model/tradeList";

type ServerActionWithId = {
  id: string;
  action: (id: string, req: unknown, type?: TradeListType) => Promise<unknown>;
  req: unknown;
  type?: TradeListType;
  after?: () => void;
};

type ServerActionWithoutId = {
  action: (type: TradeListType, req: unknown) => Promise<unknown>;
  req: unknown;
  type: TradeListType;
  after?: () => void;
};

export function ConfirmDeleteModal() {
  const [loading, setLoading] = useState<boolean>(false);
  const { modal, setModal, closeModal } = useConfirmDeleteModal();
  const {
    name,
    open,
    confirmFunc,
    loadingButton,
    mutation,
    serverAction,
    after,
  } = modal;
  const [isPending, startTransition] = useTransition();

  const isLoading = loading || mutation?.op.isPending || isPending;

  const handleClose = useCallback(() => {
    closeModal();
  }, [closeModal]);

  const handleYes = useCallback(async () => {
    if (!mutation && !confirmFunc && !serverAction) {
      throw new Error("no confirm func or mutation supplied");
    }

    if (mutation) {
      setLoading(true);
      await mutation.op.mutateAsync(mutation.req);
      setLoading(false);
      handleClose();
    }

    if (confirmFunc) {
      if (loadingButton) {
        setLoading(true);
        confirmFunc().finally(() => {
          setLoading(false);
          handleClose();
        });
      } else {
        confirmFunc().finally(() => {
          handleClose();
        });
      }
    }

    if (serverAction) {
      startTransition(async () => {
        const { action, req, type } = serverAction;

        const actionPromise =
          "id" in serverAction
            ? (action as ServerActionWithId["action"])(
                serverAction.id,
                req,
                type
              )
            : (action as ServerActionWithoutId["action"])(type!, req);

        await actionPromise.finally(() => {
          serverAction.after?.();
          handleClose();
        });
      });
    }

    if (after) {
      after();
    }
  }, [mutation, confirmFunc, serverAction, after, handleClose, loadingButton]);

  const actions = (
    <>
      <button
        type="button"
        className="btn btn-primary"
        onClick={handleClose}
        disabled={isLoading}
      >
        No
      </button>
      <button
        type="button"
        className="btn btn-outline btn-error"
        onClick={handleYes}
        disabled={isLoading}
      >
        {isLoading && <Loader2 size={ICON_SIZE} className="animate-spin" />}
        Yes
      </button>
    </>
  );

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Confirm Delete"
      actions={actions}
      maxWidth="md"
    >
      <div className="space-y-4">
        <p className="text-base-content">
          Are you sure you want to delete{" "}
          <span className="font-medium">{name}</span>?
        </p>
        <p className="text-secondary text-sm italic">
          This action is irreversible
        </p>
      </div>
    </Modal>
  );
}
